import { useState, useCallback, useEffect } from "react";
import { TextField, Button } from "@mui/material";
import styles from "./AddFamilyMemberModal.module.css";

const BROKERS = ["ANGELONE", "ZERODHA", "JM Financial"];
const BROKERS_REQUIRING_DETAILS = ["ANGELONE", "ZERODHA", "JM Financial"];

const initialForm = {
  name: "",
  broker: "",
  clientId: "",
  password: "",
  emailId: "",
  panNumber: "",
  dob: "",
  referenceName: "",
  familyTag: "",
  apiKey: "",
  apiSecretKey: "",
  totp: "",
  staticIp: "",
};

function clientToForm(client) {
  const extra = client.broker_details_extra_fields || {};
  return {
    name: client.full_name || "",
    broker: client.broker_name || "",
    clientId: client.broker_client_code || client.username || "",
    password: "",
    emailId: client.email || "",
    panNumber: client.pan_number || "",
    dob: client.dob ? client.dob.split("T")[0] : "",
    referenceName: client.reference_name || "",
    familyTag: (client.family_tag || "").toUpperCase(),
    apiKey: extra.api_key || "",
    apiSecretKey: extra.api_secret || extra.secret_key || "",
    totp: extra.totp_key || "",
    staticIp: extra.proxy_ip || "",
  };
}

function AddFamilyMemberModal({ isOpen, onClose, onAdd, editClient, onEdit }) {
  const isEdit = Boolean(editClient);
  const [form, setForm] = useState(initialForm);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen) {
      if (editClient) {
        setForm(clientToForm(editClient));
      } else {
        setForm(initialForm);
      }
      setError("");
    }
  }, [isOpen, editClient]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    const nextValue = name === "familyTag" ? value.toUpperCase() : value;
    setForm((prev) => ({ ...prev, [name]: nextValue }));
    setError("");
  }, []);

  const validate = useCallback(() => {
    if (!form.name?.trim()) return "Name is required.";
    if (!form.broker) return "Please select a Broker.";
    if (!form.clientId?.trim()) return "Client Id is required.";
    if (!isEdit && !form.password?.trim()) return "Password is required.";
    if (!form.emailId?.trim()) return "Email Id is required.";
    if (!form.panNumber?.trim()) return "Pan Number is required.";
    if (!form.dob?.trim()) return "DOB is required.";
    if (!isEdit && !form.referenceName?.trim()) return "Reference name is required.";

    const needsBrokerDetails = BROKERS_REQUIRING_DETAILS.includes(form.broker);
    if (needsBrokerDetails) {
      if (!form.apiKey?.trim()) return "API Key is required for this broker.";
      if (!form.apiSecretKey?.trim()) return "API Secret Key is required for this broker.";
      if (!form.totp?.trim()) return "TOTP is required for this broker.";
      if (!form.staticIp?.trim()) return "Static IP is required for this broker.";
    }

    return null;
  }, [form, isEdit]);

  const buildEditPayload = useCallback(() => {
    const extra = { ...(editClient.broker_details_extra_fields || {}) };
    extra.api_key = form.apiKey;
    extra.totp_key = form.totp;
    extra.proxy_ip = form.staticIp;
    if ("api_secret" in extra) extra.api_secret = form.apiSecretKey;
    if ("secret_key" in extra) extra.secret_key = form.apiSecretKey;
    return {
      full_name: form.name.trim(),
      email: form.emailId.trim(),
      broker_name: form.broker,
      is_active: editClient.is_active,
      pan_number: form.panNumber.trim(),
      dob: form.dob,
      family_tag: form.familyTag.trim(),
      broker_details_extra_fields: extra,
    };
  }, [editClient, form]);

  const buildCreatePayload = useCallback(() => {
    const extra = {
      api_key: form.apiKey,
      totp_key: form.totp,
      proxy_ip: form.staticIp,
    };
    if (form.broker === "ZERODHA") extra.api_secret = form.apiSecretKey;
    else extra.secret_key = form.apiSecretKey;
    if (form.broker === "ANGELONE") extra.trading_pin = form.password || "";
    return {
      full_name: form.name.trim(),
      broker_client_code: form.clientId.trim(),
      pan_number: form.panNumber.trim(),
      dob: form.dob,
      broker_name: form.broker,
      broker_details_extra_fields: extra,
      reference_name: form.referenceName.trim(),
      family_tag: form.familyTag.trim(),
    };
  }, [form]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      const validationError = validate();
      if (validationError) {
        setError(validationError);
        return;
      }

      if (isEdit && onEdit) {
        try {
          await onEdit(editClient.id, buildEditPayload());
          setForm(initialForm);
          setError("");
          onClose();
        } catch (err) {
          setError(err?.response?.data?.detail || "Failed to update family member");
        }
        return;
      }

      try {
        await onAdd(buildCreatePayload());
        setForm(initialForm);
        setError("");
        onClose();
      } catch (err) {
        setError(err?.response?.data?.detail || "Failed to add family member");
      }
    },
    [validate, isEdit, editClient, onEdit, buildEditPayload, buildCreatePayload, onAdd, onClose]
  );

  const handleClose = useCallback(() => {
    setForm(initialForm);
    setError("");
    onClose();
  }, [onClose]);

  const showBrokerDetails = form.broker && BROKERS_REQUIRING_DETAILS.includes(form.broker);

  if (!isOpen) return null;

  return (
    <div className={styles.overlay} onClick={handleClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h2 className={styles.title}>{isEdit ? "Edit Family Member" : "Add Family Member"}</h2>
          <button type="button" className={styles.closeBtn} onClick={handleClose} aria-label="Close">
            <svg className={styles.closeIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          {error && <p className={styles.error}>{error}</p>}

          <div className={styles.sectionLabel}>Client details</div>
          <div className={styles.sectionBlock}>
          <div className={styles.grid}>
            <div className={styles.field}>
              <label className={styles.label}>Name</label>
              <TextField
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="e.g. Namita Shah"
                fullWidth
                size="small"
                className={styles.input}
                variant="outlined"
              />
            </div>

            <div className={styles.field}>
              <label className={styles.label}>Broker</label>
              <select
                name="broker"
                value={form.broker}
                onChange={handleChange}
                className={styles.select}
                disabled={isEdit}
              >
                <option value="">Select broker</option>
                {BROKERS.map((b) => (
                  <option key={b} value={b}>
                    {b}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.field}>
              <label className={styles.label}>Client Id</label>
              <TextField
                name="clientId"
                value={form.clientId}
                onChange={handleChange}
                placeholder="e.g. 10102917"
                fullWidth
                size="small"
                className={styles.input}
                variant="outlined"
                disabled={isEdit}
              />
            </div>

            {!isEdit && (
            <div className={styles.field}>
              <label className={styles.label}>Password</label>
              <TextField
                name="password"
                type="password"
                value={form.password}
                onChange={handleChange}
                placeholder="e.g. 1234@"
                fullWidth
                size="small"
                className={styles.input}
                variant="outlined"
              />
            </div>
            )}

            <div className={styles.field}>
              <label className={styles.label}>Email Id</label>
              <TextField
                name="emailId"
                type="email"
                value={form.emailId}
                onChange={handleChange}
                placeholder="e.g. shahnamita.2004@gmail.com"
                fullWidth
                size="small"
                className={styles.input}
                variant="outlined"
              />
            </div>

            <div className={styles.field}>
              <label className={styles.label}>Pan Number</label>
              <TextField
                name="panNumber"
                value={form.panNumber}
                onChange={handleChange}
                placeholder="e.g. ABCPS9768S"
                fullWidth
                size="small"
                className={styles.input}
                variant="outlined"
              />
            </div>

            <div className={styles.field}>
              <label className={styles.label}>DOB</label>
              <TextField
                name="dob"
                type="date"
                value={form.dob}
                onChange={handleChange}
                fullWidth
                size="small"
                className={styles.input}
                variant="outlined"
                InputLabelProps={{ shrink: true }}
              />
            </div>

            {!isEdit && (
              <div className={styles.field}>
                <label className={styles.label}>Reference name</label>
                <TextField
                  name="referenceName"
                  value={form.referenceName}
                  onChange={handleChange}
                  placeholder="e.g. manali_angelone"
                  fullWidth
                  size="small"
                  className={styles.input}
                  variant="outlined"
                />
              </div>
            )}

            <div className={styles.field}>
              <label className={styles.label}>Family tag</label>
              <TextField
                name="familyTag"
                value={form.familyTag}
                onChange={handleChange}
                placeholder="e.g. FAMILY_TAG"
                inputProps={{ style: { textTransform: "uppercase" } }}
                fullWidth
                size="small"
                className={styles.input}
                variant="outlined"
              />
            </div>
          </div>
          </div>

          {showBrokerDetails && (
            <div className={styles.brokerSection}>
              <div className={styles.sectionLabel}>Broker details (required for this broker)</div>
              <div className={styles.sectionBlock}>
              <div className={styles.grid}>
                <div className={styles.field}>
                  <label className={styles.label}>API Key</label>
                  <TextField
                    name="apiKey"
                    value={form.apiKey}
                    onChange={handleChange}
                    placeholder="e.g. b7784010bc7e286e016101"
                    fullWidth
                    size="small"
                    className={styles.input}
                    variant="outlined"
                  />
                </div>
                <div className={styles.field}>
                  <label className={styles.label}>API Secret Key</label>
                  <TextField
                    name="apiSecretKey"
                    type="password"
                    value={form.apiSecretKey}
                    onChange={handleChange}
                    placeholder="e.g. Smxd251#tG"
                    fullWidth
                    size="small"
                    className={styles.input}
                    variant="outlined"
                  />
                </div>
                <div className={styles.field}>
                  <label className={styles.label}>TOTP</label>
                  <TextField
                    name="totp"
                    value={form.totp}
                    onChange={handleChange}
                    placeholder="e.g. b7784010bc7e286e016101"
                    fullWidth
                    size="small"
                    className={styles.input}
                    variant="outlined"
                  />
                </div>
                <div className={styles.field}>
                  <label className={styles.label}>Static IP</label>
                  <TextField
                    name="staticIp"
                    value={form.staticIp}
                    onChange={handleChange}
                    placeholder="e.g. 123.123.123.123"
                    fullWidth
                    size="small"
                    className={styles.input}
                    variant="outlined"
                  />
                </div>
              </div>
              </div>
            </div>
          )}

          <div className={styles.actions}>
            <Button type="button" variant="outlined" onClick={handleClose} className={styles.cancelBtn}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" className={styles.submitBtn}>
              {isEdit ? "Update" : "Add Family Member"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddFamilyMemberModal;
